import React, { useEffect, useRef, useState } from 'react';
import Appearance from 'styles/Appearance.js';

const FilePickerField = ({ fileTypes, multiple = false, onChange, onFocus, persist = true, style, value }) => {

    const [files, setFiles] = useState([]);
    const [label, setLabel] = useState('No file selected...');
    const reader = new FileReader();

    const getFileTypes = () => {

        // allow all file types if no explicit file types were requested
        if(!fileTypes || !Array.isArray(fileTypes)) {
            return '*';
        }

        // loop through file types and prepare list of accepted file types
        let types = [];
        fileTypes.forEach(type => {
            switch(type) {
                case 'csv':
                types.push('text/csv');
                break;

                case 'txt':
                types = types.concat([ 'text/plain', 'public.plain-text' ]);
                break;

                case 'doc':
                case 'docx':
                types = types.concat([ 'application/msword', 'com.microsoft.word.doc', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ]);
                break;

                case 'mov':
                types = types.concat([ 'video/quicktime', 'video/mov' ]);
                break;

                case 'mp4':
                types.push('video/mp4');
                break;

                case 'm4v':
                types.push('video/m4v');
                break;

                case 'pdf':
                types = types.concat([ 'application/pdf', 'com.adobe.pdf', 'application/x-pdf', 'application/acrobat', 'applications/vnd.pdf', 'text/pdf', 'text/x-pdf' ]);
                break;

                case 'xls':
                case 'xlsx':
                types = types.concat([
                    'application/vnd.ms-excel',
                    'com.microsoft.excel.xls',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ]);
                break;

                case 'zip':
                types.push('application/zip');
                break;

                case 'image':
                types = types.concat([
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'image/bmp',
                    'image/tiff',
                    'image/tif',
                    'image/svg+xml',
                    'image/heic',
                    'image/heif'
                ]);
                break;

                case 'video':
                types = types.concat([
                    'video/mp4',
                    'video/m4v',
                    'video/mov',
                    'video/quicktime',
                    'video/x-msvideo',
                    'video/x-matroska',
                    'video/x-flv',
                    'video/webm',
                    'video/ogg'
                ]);
                break;

                case 'jpg':
                case 'jpeg':
                types = types.concat(['image/jpeg', 'image/jpg']);
                break;

                case 'png':
                types.push('image/png');
                break;

                case 'gif':
                types.push('image/gif');
                break;

                case 'webp':
                types.push('image/webp');
                break;

                case 'bmp':
                types.push('image/bmp');
                break;

                case 'tiff':
                case 'tif':
                types = types.concat(['image/tiff', 'image/tif']);
                break;

                case 'svg':
                types.push('image/svg+xml');
                break;

                case 'heic':
                types.push('image/heic');
                break;

                case 'heif':
                types.push('image/heif');
                break;

                case 'pages':
                types.push('application/vnd.apple.pages');
                break;

                case 'avi':
                types.push('video/x-msvideo');
                break;

                case 'mkv':
                types.push('video/x-matroska');
                break;

                case 'flv':
                types.push('video/x-flv');
                break;

                case 'webm':
                types.push('video/webm');
                break;

                case 'ogg':
                types.push('video/ogg');
                break;

                case 'mp3':
                types.push('audio/mpeg');
                break;

                case 'wav':
                types.push('audio/wav');
                break;

                case 'flac':
                types.push('audio/flac');
                break;

                case 'aac':
                types.push('audio/aac');
                break;

                case 'json':
                types.push('application/json');
                break;

                case 'xml':
                types.push('application/xml');
                break;

                case 'rtf':
                types.push('application/rtf');
                break;

                default:
                // If the fileType doesn't match any predefined cases, add it as-is
                // This allows for custom MIME types to be passed through
                if(type.includes('/')) {
                    types.push(type);
                }
                break;
            }
        })

        return types.join(', ');
    }

    const onFileChange = async evt => {

        // prevent moving forward if no files were selected
        if(evt.target.files.length === 0) {
            return;
        }

        // persist content of event for later use
        evt.persist();

        // loop through files and fetch contents
        let files = [];
        for(const file of evt.target.files) {
            let result = await onReadFile(file);
            files.push(result);
        }

        // update state with selected files
        setFiles(prev => {
            if(multiple === false) {
                return files;
            }
            return prev ? prev.concat(files) : files;
        });

        // notify subscribers of files change
        if(typeof(onChange) === 'function') {
            onChange(multiple ? files : files[0]);
        }

        // clear field if persist is false
        if(persist === false) {
            evt.target.value = null;
        }
    }

    const onReadFile = async file => {

        // prepare base object for file
        let result = {
            file_name: file.name,
            file_type: file.type,
            raw: file
        };

        return new Promise(resolve => {
            reader.onload = data => {
                resolve({ ...result, data: data.target.result });
            };
            reader.readAsDataURL(file);
        });
    }

    const onUpdateLabel = () => {
        if(files.length === 0) {
            return;
        }
        if(multiple === true) {
            setLabel(files.length > 0 ? `${files.length} selected` : `No files selected...`);
            return;
        }
        setLabel(files.length > 0 ? files[0].file_name : `No file selected...`);
    }

    useEffect(() => {
        onUpdateLabel();
    }, [files]);

    useEffect(() => {
        if(!value) {
            setFiles([]);
            return;
        }
        setFiles(multiple ? value : [value]);
    }, [value]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            ...style
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                width: '100%'
            }}>
                <div
                className={`custom-file ${window.theme}`}
                style={{
                    flexGrow: 1,
                    overflow: 'hidden'
                }}>
                    <input
                    accept={getFileTypes()}
                    className={`custom-file-input ${window.theme}`}
                    multiple={multiple}
                    onChange={onFileChange}
                    onFocus={onFocus}
                    type={'file'}/>
                    <label
                    className={`custom-file-label ${window.theme}`}
                    style={{
                        color: files.length > 0 ? Appearance.colors.text() : Appearance.colors.subText(),
                        fontSize: 12,
                        fontWeight: 500
                    }}>{label}</label>
                </div>
            </div>
        </div>
    )
}

export default FilePickerField;

export const getFileIconPath = type => {
    switch(type) {

        case 'application/msword':
        case 'com.microsoft.word.doc':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return window.theme === 'dark' ? 'images/doc-icon-white.png' : 'images/doc-icon-grey.png';

        case 'application/pdf':
        case 'com.adobe.pdf':
        return window.theme === 'dark' ? 'images/pdf-icon-white.png' : 'images/pdf-icon-grey.png';

        case 'video/mp4':
        case 'video/m4v':
        case 'video/mov':
        case 'video/quicktime':
        return window.theme === 'dark' ? 'images/general-icon-white.png' : 'images/general-icon-grey.png';

        case 'application/vnd.ms-excel':
        case 'com.microsoft.excel.xls':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return window.theme === 'dark' ? 'images/xls-icon-white.png' : 'images/xls-icon-grey.png';

        default:
        return window.theme === 'dark' ? 'images/general-icon-white.png' : 'images/general-icon-grey.png';
    }
}
